import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ProtectedRoute from "@/components/ProtectedRoute";
import RouteAnalytics from "@/components/RouteAnalytics";
import useSiteSeo from "@/lib/useSiteSeo";
import CookieBanner, { readConsent } from "@/components/CookieBanner";
import { initAnalytics } from "@/lib/analytics";
import { initTracking } from "@/lib/tracking";
import { installAutoTrack } from "@/lib/autoTrack";
import { loadSeoOverrides } from "@/lib/seo";
import LandingPage from "@/pages/LandingPage";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import SocialFinishPage from "@/pages/SocialFinishPage";
import GoogleAuthCallbackPage from "@/pages/GoogleAuthCallbackPage";
import ScrollToTop from "@/components/ScrollToTop";
import { I18nProvider } from "@/contexts/I18nContext";
import LanguageSuggestionBanner from "@/components/LanguageSuggestionBanner";
import QuestionnairePage from "@/pages/QuestionnairePage";
import ReportPage from "@/pages/ReportPage";
import DashboardPage from "@/pages/DashboardPage";
import AdminPage from "@/pages/AdminPage";
import CheckoutSuccessPage from "@/pages/CheckoutSuccessPage";
import CheckoutCancelPage from "@/pages/CheckoutCancelPage";
import PrivacyPage from "@/pages/PrivacyPage";
import TermsPage from "@/pages/TermsPage";
import RefundPage from "@/pages/RefundPage";
import ContactPage from "@/pages/ContactPage";
import DataDeletionPage from "@/pages/DataDeletionPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import UnsubscribePage from "@/pages/UnsubscribePage";
import LearnIndexPage from "@/pages/LearnIndexPage";
import LearnArticlePage from "@/pages/LearnArticlePage";
import ShopPage from "@/pages/ShopPage";
import ShopProductPage from "@/pages/ShopProductPage";
import AboutPage from "@/pages/AboutPage";
import HowItWorksPage from "@/pages/HowItWorksPage";
import WhyFeelGreatsPage from "@/pages/WhyFeelGreatsPage";
import MentorSupportPage from "@/pages/MentorSupportPage";
import SciencePage from "@/pages/SciencePage";
import StoriesPage from "@/pages/StoriesPage";
import FaqPage from "@/pages/FaqPage";
import { CountryProvider } from "@/contexts/CountryContext";
import CountryPickerModal from "@/components/shop/CountryPickerModal";

// Initialise PostHog + the unified tracking facade (GA4 + registered pixels)
// only AFTER the user has consented to non-essential cookies. Returning
// visitors who already accepted will have their consent stored, so
// analytics start immediately on subsequent loads.
if (readConsent() === "all") {
  initAnalytics();
  initTracking();
}
// Install delegated click + scroll auto-tracking once per app boot.
// Each fired event is itself consent-gated inside `trackEvent`, so this is
// safe to call regardless of current consent state.
installAutoTrack();

// Load DB-backed per-route SEO overrides (set via /admin → SEO → Metadata)
// once per app boot. The cache is tiny (~1KB) and applies on top of every
// page's useSeo() output. Failure is silent — defaults win.
loadSeoOverrides();

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <I18nProvider>
            <CountryProvider>
            <ScrollToTop />
            <SiteSeoBoot />
          <RouteAnalytics />
          <Navbar />
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/auth/social-finish" element={<SocialFinishPage />} />
            <Route path="/auth/google" element={<GoogleAuthCallbackPage />} />
            <Route
              path="/questionnaire"
              element={
                <ProtectedRoute>
                  <QuestionnairePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/report"
              element={
                <ProtectedRoute>
                  <ReportPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <DashboardPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/checkout/success"
              element={
                <ProtectedRoute>
                  <CheckoutSuccessPage />
                </ProtectedRoute>
              }
            />
            <Route path="/checkout/cancel" element={<CheckoutCancelPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/refund" element={<RefundPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/data-deletion" element={<DataDeletionPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/unsubscribe" element={<UnsubscribePage />} />
            <Route path="/learn" element={<LearnIndexPage />} />
            <Route path="/learn/:slug" element={<LearnArticlePage />} />
            <Route path="/shop" element={<ShopPage />} />
            <Route path="/shop/:slug" element={<ShopProductPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/how-it-works" element={<HowItWorksPage />} />
            <Route path="/why-feelgreats" element={<WhyFeelGreatsPage />} />
            <Route path="/mentor-support" element={<MentorSupportPage />} />
            <Route path="/science" element={<SciencePage />} />
            <Route path="/stories" element={<StoriesPage />} />
            <Route path="/faq" element={<FaqPage />} />
            <Route
              path="/admin"
              element={
                <ProtectedRoute adminOnly>
                  <AdminPage />
                </ProtectedRoute>
              }
            />
          </Routes>
          <Footer />
          <Toaster position="top-center" richColors />
          <CookieBanner />
          <LanguageSuggestionBanner />
          <CountryPickerModal />
            </CountryProvider>
          </I18nProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

function SiteSeoBoot() {
  useSiteSeo();
  return null;
}

export default App;
